import React, { useEffect, useState, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import logo from "../../assets/Logo.svg";
import { useLocation, useNavigate } from "react-router-dom";
import logoutImg from "../../assets/logout.svg";
import settings from "../../assets/account_settings.svg";
import { routes } from "routes/routes";
import { useUser } from "providers/UserProvider";
import { getNameInitials } from "utils/app.util";
import dropdown from "../../assets/dropdown.svg";

const Header = ({ onLoginClick, availableDates, defaultMonth, setDateFilter, setTablePage }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [showDateDropdown, setShowDateDropdown] = useState(false);
    const [selectedDate, setSelectedDate] = useState(defaultMonth);
    const { user, logout } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const currentUrl = location.pathname;

    const handleLogout = async () => {
        try {
            await logout();
            navigate(routes.home);
            window.location.reload();
        } catch (ex) {
            console.error(ex);
        }
    };

    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    return (
        <header className="w-full font-inter border-b border-gray-200 h-[80px] flex items-center justify-center">
            <div className="w-full py-[18px] mx-auto max-w-[1200px] flex justify-between items-center max-xl:px-5">
                <div className="flex items-center justify-between w-full">
                    {/* Logo */}
                    <a href="/" className="flex items-center">
                        <img src={logo} alt="Logo" />
                    </a>

                    {/* Desktop Menu - Only show when not logged in */}
                    {!Boolean(user._id) ? (
                        <nav className="hidden md:flex md:items-center ml-[255px] mr-auto gap-[42px] text-[14px] font-medium">
                            <a
                                href="/"
                                className={`relative inline-block text-center ${
                                    location.pathname === "/"
                                        ? "after:content-[''] after:absolute after:left-1/2 after:bottom-[-13px] after:w-[10px] after:h-[4px] after:bg-[#55C880] after:rounded-full after:transform after:-translate-x-1/2 after:scale-100"
                                        : "after:scale-0"
                                } transition-transform duration-300`}
                            >
                                Home
                            </a>
                            <a
                                href="/about"
                                className={`relative inline-block text-center ${
                                    location.pathname === "/about"
                                        ? "after:content-[''] after:absolute after:left-1/2 after:bottom-[-13px] after:w-[10px] after:h-[4px] after:bg-[#55C880] after:rounded-full after:transform after:-translate-x-1/2 after:scale-100"
                                        : "after:scale-0"
                                } transition-transform duration-300`}
                            >
                                About Us
                            </a>
                            <a
                                href="/faq"
                                className={`relative inline-block text-center ${
                                    location.pathname === "/faq"
                                        ? "after:content-[''] after:absolute after:left-1/2 after:bottom-[-13px] after:w-[10px] after:h-[4px] after:bg-[#55C880] after:rounded-full after:transform after:-translate-x-1/2 after:scale-100"
                                        : "after:scale-0"
                                } transition-transform duration-300`}
                            >
                                FAQ
                            </a>
                            <a
                                href="/contact"
                                className={`relative inline-block text-center ${
                                    location.pathname === "/contact"
                                        ? "after:content-[''] after:absolute after:left-1/2 after:bottom-[-13px] after:w-[10px] after:h-[4px] after:bg-[#55C880] after:rounded-full after:transform after:-translate-x-1/2 after:scale-100"
                                        : "after:scale-0"
                                } transition-transform duration-300`}
                            >
                                Contact Us
                            </a>
                        </nav>
                    ) : (
                        (currentUrl.includes("/organization-tree") ||
                            currentUrl.includes("/dashboard") ||
                            currentUrl.includes("/account-settings")) && (
                            <nav className="hidden md:flex md:items-center ml-[165px] mr-auto gap-[42px] text-[14px] font-medium">
                                <a
                                    href="/dashboard"
                                    className={`relative inline-block text-center ${
                                        location.pathname === "/dashboard"
                                            ? "after:content-[''] after:absolute after:left-1/2 after:bottom-[-13px] after:w-[10px] after:h-[4px] after:bg-[#55C880] after:rounded-full after:transform after:-translate-x-1/2 after:scale-100"
                                            : "after:scale-0"
                                    } transition-transform duration-300`}
                                >
                                    Dashboard
                                </a>
                                <a
                                    href="/organization-tree"
                                    className={`relative inline-block text-center ${
                                        location.pathname === "/organization-tree"
                                            ? "after:content-[''] after:absolute after:left-1/2 after:bottom-[-13px] after:w-[10px] after:h-[4px] after:bg-[#55C880] after:rounded-full after:transform after:-translate-x-1/2 after:scale-100"
                                            : "after:scale-0"
                                    } transition-transform duration-300`}
                                >
                                    Organization Structure
                                </a>
                            </nav>
                        )
                    )}

                    <div className="">
                        {user._id ? (
                            <div className="flex gap-3 items-center">
                                {currentUrl.includes("dashboard") && (
                                    <div className="relative">
                                        <div className="flex items-center gap-1">
                                            <span className="text-[12px] font-semibold">
                                                Data View:{" "}
                                            </span>
                                            <div
                                                className="flex items-center border border-[#D9D9D9] rounded-[6px] gap-2 px-2 py-1 justify-between cursor-pointer min-w-[99px] h-[30px]"
                                                onClick={() =>
                                                    setShowDateDropdown(!showDateDropdown)
                                                }
                                            >
                                                <span className="text-[14px]">{selectedDate}</span>
                                                <img src={dropdown} alt="" className="" />
                                            </div>
                                        </div>
                                        <div className="absolute left-[66px] top-8 max-h-[200px] overflow-y-auto border border-[#D9D9D9] rounded-[6px]">
                                            {showDateDropdown &&
                                                availableDates.map((date, index) => (
                                                    <div
                                                        key={date.month}
                                                        className={`flex items-center border border-[#D9D9D9] bg-white px-2 py-1 gap-2 cursor-pointer w-[99px] h-[30px] ${
                                                            index === 0
                                                                ? "rounded-t-[6px]"
                                                                : index ===
                                                                      availableDates.length - 1 &&
                                                                  "rounded-b-[6px]"
                                                        }`}
                                                        onClick={() => {
                                                            setDateFilter(date.month);
                                                            setShowDateDropdown(false);
                                                            setSelectedDate(date.formattedDate);
                                                            setTablePage([1, 1, 1]);
                                                        }}
                                                    >
                                                        <span className="text-[14px]">
                                                            {date.formattedDate}
                                                        </span>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}

                                {currentUrl.includes("/dashboard") && (
                                    <div className="h-[16px] bg-[#D9D9D9] ml-[7px] w-[1.5px]"></div>
                                )}

                                <div className="relative">
                                    <button
                                        ref={buttonRef}
                                        onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                                        className="flex items-center gap-3 p-2 pl-0 rounded hover:bg-gray-50"
                                    >
                                        <div className="text-sm w-10 h-10 bg-[#F4F5F6] rounded-full flex items-center justify-center text-[#55C880]">
                                            {getNameInitials(user.firstName, user.lastName)}
                                        </div>
                                        <div className="md:flex md:flex-col md:items-start hidden">
                                            <span className="text-sm font-medium text-gray-800">
                                                {`${user.firstName} ${user.lastName}`}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {user.email}
                                            </span>
                                        </div>
                                        <ChevronDown className="w-4 h-4 text-gray-500 hidden md:block" />
                                    </button>

                                    {/* Profile Dropdown */}
                                    {showProfileDropdown && (
                                        <div
                                            ref={dropdownRef}
                                            className="bg-white absolute right-0 top-11 mt-2 w-[249px] shadow-lg py-1 z-[999] rounded-[10px] border border-[#D9D9D9]"
                                        >
                                            <div className="flex flex-col justify-center items-center my-5">
                                                <div className="w-[70px] h-[70px] flex items-center justify-center text-[#55C880] bg-[#F4F5F6] rounded-full text-[20px] font-semibold mb-3">
                                                    {getNameInitials(user.firstName, user.lastName)}
                                                </div>
                                                <div className="text-[18px] font-semibold">
                                                    {`${user.firstName} ${user.lastName}`}
                                                </div>
                                                <div className="text-[12px] text-[#5F6368]">
                                                    {user.email}
                                                </div>
                                            </div>
                                            <div className="border-t border-[#D9D9D9] space-y-1 py-5 px-4">
                                                <button
                                                    onClick={() => navigate("/account-settings")}
                                                    className="flex items-center gap-2 w-full text-left px-4 text-sm"
                                                >
                                                    <img src={settings} alt="" className="" />
                                                    Account Settings
                                                </button>
                                                <button
                                                    onClick={handleLogout}
                                                    className="flex items-center gap-2 w-full text-left px-4 text-sm"
                                                >
                                                    <img src={logoutImg} alt="" className="" />
                                                    Logout
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <button
                                    className="font-inter font-medium hidden md:block text-[14px] bg-[#55C880] text-white px-[16px] py-[8px] rounded-[10px]"
                                    onClick={onLoginClick}
                                >
                                    Login
                                </button>
                            </>
                        )}
                    </div>
                    {/* Mobile Menu Button - Only show when not logged in */}
                    {!Boolean(user._id) && (
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="md:hidden p-2"
                        >
                            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    )}
                </div>
            </div>

            {/* Mobile Menu - Only show when not logged in */}
            {!Boolean(user._id) && isMenuOpen && (
                <div className="md:hidden absolute top-20 left-0 right-0 bg-white shadow-lg py-4 px-6 z-50">
                    <nav className="flex flex-col space-y-4">
                        <a href="/" className="font-semibold" onClick={() => setIsMenuOpen(false)}>
                            Home
                        </a>
                        <a
                            href="/about"
                            className="font-semibold"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            About Us
                        </a>
                        <a
                            href="/faq"
                            className="font-semibold"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            FAQ
                        </a>
                        <a
                            href="/contact"
                            className="font-semibold"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            Contact Us
                        </a>
                        <button
                            className="font-inter md:block font-medium text-[14px] bg-[#55C880] text-white px-[15px] py-[8px] rounded-[10px]"
                            onClick={onLoginClick}
                        >
                            Login
                        </button>
                    </nav>
                </div>
            )}
        </header>
    );
};

export default Header;
